export function hexToRgba(hex: string, alpha: number): string {
  const sanitized = hex.replace('#', '')
  const bigint = parseInt(sanitized, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export function parseRgba(rgba: string): { hex: string; alpha: number } | null {
  const match = rgba.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/)
  if (!match) return null
  const r = parseInt(match[1], 10)
  const g = parseInt(match[2], 10)
  const b = parseInt(match[3], 10)
  const a = match[4] !== undefined ? parseFloat(match[4]) : 1
  const hex = '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')
  return { hex, alpha: a }
}

type ColorPickerFieldProps = {
  label: string
  value: string
  supportsAlpha?: boolean
  onChange: (v: string) => void
}

export function ColorPickerField({
  label,
  value,
  supportsAlpha,
  onChange,
}: ColorPickerFieldProps) {
  const isRgba = value.startsWith('rgba(')
  const parsed = isRgba ? parseRgba(value) : null
  const hexValue = parsed ? parsed.hex : value.startsWith('#') ? value : '#5e6ad2'
  const alphaValue = parsed ? Math.round(parsed.alpha * 100) : 100

  const handleHexChange = (newHex: string) => {
    if (supportsAlpha && isRgba) {
      onChange(hexToRgba(newHex, alphaValue / 100))
    } else {
      onChange(newHex)
    }
  }

  const handleAlphaChange = (newAlpha: number) => {
    onChange(hexToRgba(hexValue, newAlpha / 100))
  }

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-text-muted text-[11px] font-medium uppercase tracking-wider">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={hexValue}
          onChange={(e) => handleHexChange(e.target.value)}
          className="w-9 h-9 rounded-md cursor-pointer border border-white/[0.06] bg-transparent p-0.5"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-white/[0.04] border border-white/[0.06] text-text-primary rounded-md px-3 py-2 text-xs font-mono outline-none focus:border-accent transition-colors"
          placeholder="#5e6ad2"
        />
        {supportsAlpha && (
          <div className="flex items-center gap-1.5">
            <input
              type="range"
              min={0}
              max={100}
              value={alphaValue}
              onChange={(e) => handleAlphaChange(parseInt(e.target.value, 10))}
              className="w-16 h-1.5 accent-accent"
            />
            <span className="text-[10px] text-text-muted font-mono w-8 text-right">{alphaValue}%</span>
          </div>
        )}
        <div
          className="w-9 h-9 rounded-md border border-white/[0.06] shrink-0"
          style={{ backgroundColor: value }}
        />
      </div>
    </div>
  )
}
